import React, { useEffect, useRef } from 'react';
import './UnityGame.css';
import unityConfig from '../config/unity-config';

const UnityGame = () => {
  const canvasRef = useRef(null);
  const loadingBarRef = useRef(null);
  const progressBarRef = useRef(null);
  const warningBannerRef = useRef(null);
  const fullscreenButtonRef = useRef(null);

  useEffect(() => {
    // Shows a temporary message banner/ribbon for a few seconds, or
    // a permanent error message on top of the canvas if type=='error'.
    // If type=='warning', a yellow highlight color is used.
    const unityShowBanner = (msg, type) => {
      const warningBanner = warningBannerRef.current;
      function updateBannerVisibility() {
        warningBanner.style.display = warningBanner.children.length ? 'block' : 'none';
      }
      const div = document.createElement('div');
      div.innerHTML = msg;
      warningBanner.appendChild(div);
      if (type === 'error') div.style = 'background: red; padding: 10px;';
      else {
        if (type === 'warning') div.style = 'background: yellow; padding: 10px;';
        setTimeout(function() {
          warningBanner.removeChild(div);
          updateBannerVisibility();
        }, 5000);
      }
      updateBannerVisibility();
    };

    const canvas = canvasRef.current;
    const loadingBar = loadingBarRef.current;
    const progressBarFull = progressBarRef.current;
    const fullscreenButton = fullscreenButtonRef.current;

    // Get configuration from imported unityConfig
    const baseUrl = unityConfig.baseUrl;
    const buildUrl = `${baseUrl}/Build`;
    const loaderUrl = `${buildUrl}/${unityConfig.buildFiles.loaderHash}.loader.js`;
    
    const config = {
      arguments: [],
      dataUrl: `${buildUrl}/${unityConfig.buildFiles.dataHash}.data`,
      frameworkUrl: `${buildUrl}/${unityConfig.buildFiles.frameworkHash}.framework.js`,
      codeUrl: `${buildUrl}/${unityConfig.buildFiles.wasmHash}.wasm`,
      streamingAssetsUrl: `${baseUrl}/StreamingAssets`,
      companyName: unityConfig.productInfo.companyName,
      productName: unityConfig.productInfo.productName,
      productVersion: unityConfig.productInfo.productVersion,
      showBanner: unityShowBanner,
    };
    
    // Only use Brotli (.br) files in production, not in local development
    // Local development servers often don't serve .br files with correct headers
    const isLocalhost = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';
    const isBrowserSupportsBrotli = 'fetch' in window && 'ReadableStream' in window;
    
    if (isBrowserSupportsBrotli && !isLocalhost && process.env.NODE_ENV === 'production') {
      config.dataUrl += ".br";
      config.frameworkUrl += ".br";
      config.codeUrl += ".br";
    }

    // Unity cache fix for hosting services
    window.disableUnityCache = unityConfig.disableUnityCache;
    
    // Add loading progress enhancements
    const loadingText = document.createElement('div');
    loadingText.className = 'unity-loading-text';
    loadingText.style.color = '#ffffff';
    loadingText.style.position = 'absolute';
    loadingText.style.width = '100%';
    loadingText.style.textAlign = 'center';
    loadingText.style.bottom = '60px';
    loadingText.style.fontSize = '14px';
    loadingBar.appendChild(loadingText);
    
    // Set up for mobile or desktop
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      // Mobile device style
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, height=device-height, initial-scale=1.0, user-scalable=no, shrink-to-fit=yes';
      document.getElementsByTagName('head')[0].appendChild(meta);
      canvas.className = "unity-mobile";
    } else {
      // Desktop style
      canvas.style.width = `${unityConfig.canvasDimensions.width}px`;
      canvas.style.height = `${unityConfig.canvasDimensions.height}px`;
    }

    loadingBar.style.display = "block";
    
    // Load the Unity loader script
    const script = document.createElement("script");
    script.src = loaderUrl;
    
    // Fix for CORS issues with React dev server
    script.crossOrigin = "anonymous";
    
    script.onload = () => {
      if (typeof window.createUnityInstance !== 'function') {
        console.error('Unity WebGL loader failed to define createUnityInstance');
        unityShowBanner('Unity WebGL loader failed to initialize. Please check the browser console for more information.', 'error');
        return;
      }

      window.createUnityInstance(canvas, config, (progress) => {
        progressBarFull.style.width = 100 * progress + "%";
        
        // Loading progress tracking
        const currentWidth = 100 * progress;
        loadingText.textContent = 'Loading: ' + Math.round(currentWidth) + '%';
      }).then((unityInstance) => {
        loadingBar.style.display = "none";
        window.unityInstance = unityInstance; // Store for global access - important for Firebase hosting
        
        if (fullscreenButton) {
          fullscreenButton.onclick = () => {
            unityInstance.SetFullscreen(1);
          };
        }
      }).catch((message) => {
        console.error('Unity WebGL error:', message);
        unityShowBanner('Failed to load Unity content: ' + message, 'error');
      });
    };

    script.onerror = (event) => {
      console.error('Unity loader script failed to load:', event);
      unityShowBanner('Failed to load Unity WebGL loader script. Please check your internet connection.', 'error');
    };

    document.body.appendChild(script);
    
    // Memory management and tab visibility
    document.addEventListener('visibilitychange', function() {
      if (document.hidden && window.unityInstance) {
        window.unityInstance.SetFullscreen(0);
      }
    });
    
    return () => {
      // Cleanup function
      document.removeEventListener('visibilitychange', () => {});
      if (window.unityInstance) {
        window.unityInstance = null;
      }
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="unity-wrapper">
      <div id="unity-container" className="unity-desktop">
        <canvas 
          ref={canvasRef} 
          id="unity-canvas" 
          width={unityConfig.canvasDimensions.width} 
          height={unityConfig.canvasDimensions.height} 
          tabIndex="-1"
        ></canvas>
        <div ref={loadingBarRef} id="unity-loading-bar">
          <div id="unity-logo"></div>
          <div id="unity-progress-bar-empty">
            <div ref={progressBarRef} id="unity-progress-bar-full"></div>
          </div>
        </div>
        <div ref={warningBannerRef} id="unity-warning"></div>
        <div id="unity-footer">
          <div id="unity-logo-title-footer"></div>
          <div ref={fullscreenButtonRef} id="unity-fullscreen-button"></div>
          <div id="unity-build-title">{unityConfig.productInfo.productName}</div>
        </div>
      </div>
    </div>
  );
};

export default UnityGame; 